import {Routes, UrlMatchResult, UrlSegment} from '@angular/router';
import {cmsPageResolver} from './pages/content/cms-page-resolver';
import {ContentShellComponent} from './pages/content/content-shell.component';
import {layoutResolver} from './pages/content/content.routes';
import {AutomatedPaymentComponent} from "./pages/automated-payment/automated-payment.component";
import {AuthGuardKeyCloak} from "./AuthGuardKeyCloak";

const CMS_PREFIXES = [
  '', // homepage
  'prodotti-per-te',
  'landing-page',
  'chi-siamo',
  'partners',
  'lavora-con-noi',
  'reclami',
  'contenuti-utili',
  'cosa-fare-in-caso-di-sinistro',
  'accessibilita',
  'dati-societari-insurance',
  'dati-societari-services',
  'privacy',
  'privacy-services',
  'elenco-agenzie',
  'intermediari-redion-italy-services',
  'informativa-conflitti-di-interesse',
  'arbitro-assicurativo-aas',
  'fea',
  'DataBreachServices',
  'DataBreachItaly'
];


const EXCLUDED_PREFIXES = [
  'account',
  'funnel',
  'area-riservata',
  'azienda',
  'contatti',
  'set-informativi',
  'europ-assistance-diventa-redion',
  '404',
  'pay-by-link'
];


// Matches if the URL starts with one of the prefixes above.
// Return `consumed` = the entire URL so the feature can decide subpaths.
export function cmsMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length === 0) {
    // '' root – match homepage
    return { consumed: segments };
  }

  const first = segments[0].path;


// ✅ escludi le route Angular “vere”
  if (EXCLUDED_PREFIXES.includes(first)) {
    return null;
  }


  if (!CMS_PREFIXES.includes(first)) {
    return null;
  }

  const slug = segments.map(s => s.path).join('/');

  return {
    consumed: segments,
    posParams: {
      slugPath: new UrlSegment(slug, {}),
    },
  };
}



export const routes: Routes = [


  {
    path: 'funnel',
    loadChildren: () =>
      import('./pages/funnel/funnel.routes').then(m => m.FUNNEL_ROUTES),
  },

  {
    path: 'area-riservata',
    canActivate: [AuthGuardKeyCloak],
    loadChildren: () => import('./pages/area-riservata/PRIVATE_AREA_ROUTES').then(mod => mod.AREA_RISERVATA_ROUTES)
  },

  {
    path: 'pay-by-link',
    component: ContentShellComponent,
    resolve: { layout: layoutResolver },
    children: [
      {
        path: '',
        component: AutomatedPaymentComponent
      }
    ]
  },

  {
    path: 'azienda',
    loadChildren: () => import('./pages/azienda/azienda-route').then(mod => mod.AZIENDA_ROUTES)
  },
  {
    path: 'privacy-cookie-redion-italy-insurance',
    component: ContentShellComponent,
    resolve: { layout: layoutResolver },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/privacy/cookies/cookies.component').then(m => m.CookiesComponent)
      }
    ]
  },
  {
    path: 'privacy-cookie-redion-italy-services',
    component: ContentShellComponent,
    resolve: { layout: layoutResolver },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/privacy/cookies/cookies.component').then(m => m.CookiesComponent)
      }
    ]
  },
  {
    path: 'DataBreachItaly',
    component: ContentShellComponent,
    resolve: { layout: layoutResolver },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/data-breach/data-breach.component').then(m => m.DataBreachComponent)
      }
    ]
  },
  {
    path: 'DataBreachServices',
    component: ContentShellComponent,
    resolve: { layout: layoutResolver },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/data-breach-vai/data-breach-vai.component').then(m => m.DataBreachVaiComponent)
      }
    ]
  },
  {
    path: 'europ-assistance-diventa-redion',
    component: ContentShellComponent,
    resolve: { layout: layoutResolver },
    children: [
      {
        path: '',
        resolve: { page: cmsPageResolver },
        loadComponent: () =>
          import('./pages/lp-magnolia/lp-magnolia.component').then(m => m.LpMagnoliaComponent)
      }
    ]
  },
  {
    path: 'reclami',
    component: ContentShellComponent,
    resolve: { layout: layoutResolver },
    children: [
      {
        path: '',
        resolve: { page: cmsPageResolver },
        loadComponent: () =>
          import('./pages/content/content.component').then(m => m.ContentComponent)
      }
    ]
  },
  {
    path: 'set-informativi',
    component: ContentShellComponent,
    resolve: { layout: layoutResolver },
    children: [
      {
        path: '',
        resolve: { page: cmsPageResolver },
        loadComponent: () =>
          import('./pages/set-informativi/components/set-informativi.component').then(m => m.SetInformativiComponent)
      }
    ]
  },
  {
    path: 'set-informativi-collettivi',
    component: ContentShellComponent,
    resolve: { layout: layoutResolver },
    children: [
      {
        path: '',
        resolve: { page: cmsPageResolver },
        loadComponent: () =>
          import('./pages/set-informativi/components/set-informativi.component').then(m => m.SetInformativiComponent)
      }
    ]
  },
  {
    path: 'contatti',
    loadChildren: () =>
      import('./pages/contatti/contatti-routes').then(m => m.CONTATTI_ROUTES),
  },
  {
    path: 'account',
    loadChildren: () =>
      import('./pages/account/account-routes').then(m => m.ACCOUNT_ROUTE),
  },
  {
    path: 'dev/aria-form',
    component: ContentShellComponent,
    resolve: { layout: layoutResolver },
    title: 'Sandbox Angular Aria Form',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/dev/aria-form-sandbox/aria-form-sandbox.component').then(
            (m) => m.AriaFormSandboxComponent
          ),
      },
    ],
  },
  {
    path: 'contenuti-utili',
    loadChildren: () => import('./pages/contenuti-utili/contenuti-utili.routes')
      .then(m => m.CONTENUTI_UTILI_ROUTES)
  },
  {
    path: '404',
    component: ContentShellComponent,
    resolve: { layout: layoutResolver },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/not-found/not-found.component').then(m => m.NotFoundComponent)
      }
    ]
  },
  {
    matcher: cmsMatcher,
    loadChildren: () =>
      import('./pages/content/content.routes').then(m => m.CONTENT_ROUTES),
    data: { preload: true },
  },
  // Optional: NotFound fallback (keep at the end)
  { path: '**', loadChildren: () =>
      import('./pages/content/content.routes').then(m => m.CONTENT_ROUTES),
    data: { preload: true }, }
];
